import { X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { useHints } from './hints';
import { LearnMore } from './learn-more';

export interface TourStep {
  /** CSS selector of the element to point at; omitted = centered card. */
  target?: string;
  title: string;
  body: ReactNode;
  learnMore?: string;
}
export interface TourDef {
  id: string;
  steps: TourStep[];
}

interface TourApi {
  start(tour: TourDef): void;
  /** Starts the tour on first visit only (unless dismissed or hints are off). */
  offer(tour: TourDef): void;
  active: string | null;
  /** The current screen's tour, for "Take the tour" in the ? menu. */
  current: TourDef | null;
  setCurrent(tour: TourDef | null): void;
}
const TourContext = createContext<TourApi | null>(null);
export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour needs <TourProvider>');
  return ctx;
};

/** Registers a screen's tour: offered on first visit, re-openable from the ? menu. */
export function useScreenTour(tour: TourDef) {
  const { offer, setCurrent } = useTour();
  // biome-ignore lint/correctness/useExhaustiveDependencies: register and offer once per tour id
  useEffect(() => {
    setCurrent(tour);
    offer(tour);
    return () => setCurrent(null);
  }, [tour.id]);
}

export function TourProvider({ children, onFinish }: { children: ReactNode; onFinish?: (tourId: string) => void }) {
  const hints = useHints();
  const [tour, setTour] = useState<TourDef | null>(null);
  const [step, setStep] = useState(0);
  const [current, setCurrent] = useState<TourDef | null>(null);
  const start = useCallback((t: TourDef) => {
    setTour(t);
    setStep(0);
  }, []);
  const offer = useCallback(
    (t: TourDef) => {
      if (hints.showHints && !hints.isDismissed(`tour:${t.id}`)) start(t);
    },
    [hints, start],
  );
  const close = useCallback(() => {
    if (tour) {
      hints.dismiss(`tour:${tour.id}`);
      onFinish?.(tour.id);
    }
    setTour(null);
  }, [tour, hints, onFinish]);
  const api = useMemo(() => ({ start, offer, active: tour?.id ?? null, current, setCurrent }), [start, offer, tour, current]);
  return (
    <TourContext.Provider value={api}>
      {children}
      {tour && <TourCard tour={tour} step={step} setStep={setStep} close={close} />}
    </TourContext.Provider>
  );
}

function TourCard({ tour, step, setStep, close }: { tour: TourDef; step: number; setStep: (n: number) => void; close: () => void }) {
  const s = tour.steps[step];
  const cardRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    const el = s?.target ? document.querySelector(s.target) : null;
    el?.scrollIntoView({ block: 'nearest' });
    setRect(el ? el.getBoundingClientRect() : null);
  }, [s]);
  // The card takes focus when it opens; when the tour closes, focus goes back to where it was (WCAG 2.4.3).
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      const lost = !document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected;
      if (lost && before?.isConnected && before !== document.body) before.focus();
    };
  }, []);
  useEffect(() => {
    cardRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  if (!s) return null;
  const last = step === tour.steps.length - 1;
  const pos = rect
    ? { top: Math.min(rect.bottom + 12, window.innerHeight - 220), left: Math.max(12, Math.min(rect.left, window.innerWidth - 340)) }
    : { top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 160 };
  return (
    <>
      {rect && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-50 rounded-md ring-2 ring-foreground ring-offset-2 ring-offset-background"
          style={{ top: rect.top - 2, left: rect.left - 2, width: rect.width + 4, height: rect.height + 4 }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-title"
        tabIndex={-1}
        className="fixed z-50 w-80 space-y-2 rounded-lg border bg-surface-raised p-4 text-sm shadow-lg"
        style={pos}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id="tour-title" className="font-semibold">
            {s.title}
          </h2>
          <button
            type="button"
            aria-label="Close tour"
            onClick={close}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="text-muted-foreground">{s.body}</div>
        {s.learnMore && <LearnMore slug={s.learnMore} />}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground tabular">
            {step + 1} / {tour.steps.length}
          </span>
          <div className="flex gap-2">
            {step > 0 && (
              <Button size="sm" variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            <Button size="sm" onClick={() => (last ? close() : setStep(step + 1))}>
              {last ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
